import React, { useState } from "react"
import useApi from "../../../hooks/useApi.js"

function ProposalRow({ t, index, experiment, onApprove, onDismiss, busy }) {
  return (
    <li className="px-6 py-[22px] border-b border-rule last:border-b-0 grid grid-cols-[36px_minmax(0,1fr)_auto] gap-6 items-start">
      <span
        className="font-mono text-[12px] text-ink-faint pt-[3px]"
        style={{ fontVariantNumeric: "tabular-nums", letterSpacing: "0.04em" }}
      >
        {String(index + 1).padStart(2, "0")}
      </span>

      <div>
        <h3 className="text-[16px] font-medium tracking-[-0.015em] text-ink-primary">
          {experiment.name}{experiment.name.endsWith(".") ? "" : "."}
        </h3>
        {experiment.ai_reasoning && (
          <p className="mt-2 text-[13px] text-ink-secondary" style={{ lineHeight: 1.55 }}>
            {experiment.ai_reasoning}
          </p>
        )}
        <p
          className="mt-3 font-mono text-[11px] text-ink-faint"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {experiment.target_selector} · {experiment.page_path}
        </p>
      </div>

      <div className="flex flex-col gap-2 items-end">
        <button
          type="button"
          onClick={() => onApprove(experiment)}
          disabled={busy}
          className="bg-brand text-ink-on-dark rounded-button px-4 py-2 text-[13px] font-medium disabled:opacity-60"
        >
          {busy === "approve"
            ? t("dashboard.overview.proposals_approving")
            : t("dashboard.overview.proposals_review")}
        </button>
        <button
          type="button"
          onClick={() => onDismiss(experiment)}
          disabled={busy}
          className="text-[13px] text-ink-tertiary hover:text-failure disabled:opacity-60"
        >
          {busy === "dismiss"
            ? t("dashboard.overview.proposals_dismissing")
            : t("dashboard.overview.proposals_dismiss")}
        </button>
      </div>
    </li>
  )
}

export default function ProposalsBlock({ t, experiments, onChange }) {
  const api = useApi()
  const [busyId, setBusyId] = useState(null) // { id, op }
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState(null)

  const proposals = experiments.filter((e) => e.status === "pending_approval")

  const approve = async (experiment) => {
    setBusyId({ id: experiment.id, op: "approve" })
    setError(null)
    try {
      await api.post(`/api/v1/shop/experiments/${experiment.id}/approve`)
      onChange()
    } catch (err) { setError(err.message) }
    finally { setBusyId(null) }
  }

  const dismiss = async (experiment) => {
    setBusyId({ id: experiment.id, op: "dismiss" })
    setError(null)
    try {
      await api.post(`/api/v1/shop/experiments/${experiment.id}/archive`)
      onChange()
    } catch (err) { setError(err.message) }
    finally { setBusyId(null) }
  }

  const generate = async () => {
    setGenerating(true)
    setError(null)
    try {
      await api.post("/api/v1/shop/experiments/generate")
      // Generation is async (Sidekiq). Don't refresh immediately — user can
      // reload in 30-60s when proposals appear.
    } catch (err) { setError(err.message) }
    finally { setGenerating(false) }
  }

  return (
    <section>
      <p
        className="font-mono text-[10px] text-ai mb-3"
        style={{ letterSpacing: "0.15em", fontVariantNumeric: "tabular-nums" }}
      >
        {t("dashboard.overview.eyebrow_proposals")}
      </p>

      <div className="bg-surface-card border border-rule rounded-card">
        {proposals.length === 0 ? (
          <div className="px-6 py-8 space-y-4">
            <p className="text-sm text-ink-secondary">{t("dashboard.overview.proposals_empty")}</p>
            <button
              type="button"
              onClick={generate}
              disabled={generating}
              className="bg-surface-card border border-rule rounded-button px-4 py-2 text-[13px] font-medium text-ink-primary disabled:opacity-60"
            >
              {generating
                ? t("dashboard.overview.proposals_generating")
                : t("dashboard.overview.proposals_generate")}
            </button>
          </div>
        ) : (
          <ul>
            {proposals.map((exp, i) => (
              <ProposalRow
                key={exp.id}
                t={t}
                index={i}
                experiment={exp}
                onApprove={approve}
                onDismiss={dismiss}
                busy={busyId?.id === exp.id ? busyId.op : null}
              />
            ))}
          </ul>
        )}
      </div>
      {error && <p className="mt-2 text-sm text-failure">{error}</p>}
    </section>
  )
}
